import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CollectionResponse,
  CreateDeclarationTodoPayload,
  CreditRequestOverviewItem,
  CsiCode,
  DcsphCode,
  Declaration,
  DeclarationDashboardStats,
  DeclarationIdentifier,
  DeclarationProviderOption,
  DeclarationTodos,
  DeclarationTreatmentPractitioner,
  EmptyResponse,
  FilterByQuery,
  IncludeQuery,
  InvoiceDetails,
  JournalInvoicePreview,
  InvoicePlatformSyncListItem,
  InvoiceReturn,
  MissingJournalReminder,
  LocationIdentifier,
  OpenJournalAppointment,
  PaginationQuery,
  PaginationResponse,
  Rate,
  Response,
  SortByQuery,
  ToBeReinvoicedInvoice,
  UpdateDeclarationPayload,
} from "~/types/api";
import type { PaymentStatus } from "~/constants/payment-status";

/** A Vecozo credit request to correct a single approved declaration (one session). */
export interface DeclarationCorrection {
  id: string;
  invoice_number: string;
  status: "pending" | "approved" | "rejected" | "completed";
  credit_reference_number: string;
  requested_changes: {
    product_id: number;
    practitioner_id: string;
    practitioner_role_id: string;
    appointment_date: string;
    csi_code: string;
  };
  new_invoice_number: string | null;
  error_message: string | null;
  requested_at: string;
  processed_at: string | null;
  completed_at: string | null;
}

export function createDeclarationApi({
  getRequest,
  postRequest,
  putRequest,
  patchRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Validates a DCSPH code for correctness. */
  const validateDcsphCode = (dcspCode: string) => getRequest<Response<DcsphCode>>(`declarations/dcsph/${dcspCode}`);

  /** Looks up a DCSPH code by complaint and diagnosis combination. */
  const lookupDcsphCode = (complaint: string, diagnosis: string) =>
    getRequest<Response<DcsphCode>>(
      `/declarations/dcsph/${encodeURIComponent(complaint)}/${encodeURIComponent(diagnosis)}`,
    );

  /** Fetches DCSPH codes filtered by a partial code string. */
  const fetchDcsphCodes = (code: string) =>
    getRequest<CollectionResponse<DcsphCode>>(`/declarations/dcsph/filter?code=${code}`);

  /** Fetches all available DCSPH codes. */
  const fetchAllDcsphCodes = () => getRequest<CollectionResponse<DcsphCode>>("/declarations/dcsph");

  /** Fetches declaration dashboard statistics with optional date and location filters. */
  const fetchDeclarationDashboardStats = (params?: {
    from?: string;
    to?: string;
    date_field?: string;
    location_id?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.from) query.set("from", params.from);
    if (params?.to) query.set("to", params.to);
    if (params?.date_field) query.set("date_field", params.date_field);
    if (params?.location_id) query.set("location_id", String(params.location_id));
    const queryString = query.toString();
    return getRequest<DeclarationDashboardStats>(
      `/declarations/dashboard-stats${queryString ? `?${queryString}` : ""}`,
    );
  };

  /** Sends a missing-journal notification to an employee. */
  const notifyEmployeeMissingJournals = (data: { practitioner_role_id: string; missing_journal_count: number }) =>
    postRequest<{ message: string; reminder: MissingJournalReminder }>("/declarations/missing-journals/notify", data);

  /** Fetches appointments that are missing journal entries. */
  const fetchMissingJournalAppointments = (params?: { from?: string; to?: string; location_id?: number }) => {
    const query = new URLSearchParams();
    if (params?.from) query.set("from", params.from);
    if (params?.to) query.set("to", params.to);
    if (params?.location_id) query.set("location_id", String(params.location_id));
    const queryString = query.toString();
    return getRequest<Response<OpenJournalAppointment[]> & { reminders: MissingJournalReminder[] }>(
      `/declarations/missing-journals${queryString ? `?${queryString}` : ""}`,
    );
  };

  /** Fetches invoices that need to be reinvoiced after a credit. */
  const fetchToBeReinvoicedInvoices = (params?: { from?: string; to?: string; location_id?: number }) => {
    const query = new URLSearchParams();
    if (params?.from) query.set("from", params.from);
    if (params?.to) query.set("to", params.to);
    if (params?.location_id) query.set("location_id", String(params.location_id));
    const queryString = query.toString();
    return getRequest<Response<ToBeReinvoicedInvoice[]>>(
      `/declarations/to-be-reinvoiced${queryString ? `?${queryString}` : ""}`,
    );
  };

  /** Reinvoices a previously credited invoice. */
  const reinvoiceCreditedInvoice = (invoiceNumber: string) =>
    postRequest<{ invoice_number: string }>(
      `/declarations/to-be-reinvoiced/${encodeURIComponent(invoiceNumber)}/reinvoice`,
      {},
    );

  /** Fetches a paginated list of declarations. */
  const fetchDeclarations = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<Declaration>("/declarations", pagination, sortBy, includes, filterBy);

  const fetchDeclarationTreatmentPractitioners = (locations: LocationIdentifier[] = [], signal?: AbortSignal) =>
    getRequest<CollectionResponse<DeclarationTreatmentPractitioner>>("/declarations/treatment-practitioners", {
      params: locations.length ? { "filter[location_id]": locations.join(",") } : {},
      signal,
    });

  /** Fetches a paginated list of declaration batches. */
  const fetchDeclarationBatches = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest("/declarations/batches", pagination, sortBy, includes, filterBy);

  /** Fetches a single declaration batch by ID. */
  const fetchDeclarationBatch = async (batchId: string) => {
    const response = await getRequest<{ data: unknown }>(`/declarations/batches/${batchId}`);
    return response.data;
  };

  /** Fetches the Vecozo response and request XML of a whole-batch-rejected declaration batch. */
  const fetchDeclarationBatchRejectionXml = async (batchId: string) => {
    const response = await getRequest<{
      data: {
        response: { filename: string; content: string };
        request: { filename: string; content: string } | null;
      };
    }>(`/declarations/batches/${batchId}/rejection-xml`);
    return response.data;
  };

  /** Resolves a `ZorgtrajectNummer` from a rejected declaration message back to its treatment. */
  const fetchDeclarationBatchRejectionTreatment = async (batchId: string, identifier: string) => {
    const response = await getRequest<{
      data: {
        id: number;
        patient_id: number;
        patient_name: string | null;
        started_at: string | null;
        referrer_name: string | null;
        referrer_agb_code: string | null;
        referred_date: string | null;
      };
    }>(`/declarations/batches/${batchId}/rejection-treatment/${encodeURIComponent(identifier)}`);
    return response.data;
  };

  /** Retries all failed declarations in a batch. */
  const retryFailedDeclarationBatch = (batchId: string) =>
    postRequest<Response<{ id: string; reference_number: string }>>(`/declarations/batches/${batchId}/retry`);

  /** Reprocesses a declaration batch, regenerating its declarations from source data. */
  const reprocessDeclarationBatch = (batchId: string) =>
    postRequest<Response<unknown>>(`/declarations/batches/${batchId}/reprocess`);

  /** Fetches a paginated list of invoice returns. */
  const fetchInvoiceReturns = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<InvoiceReturn>("/declarations/returns", pagination, sortBy, includes, filterBy);

  /** Fetches the count of unprocessed invoice returns. */
  const fetchInvoiceReturnsUnprocessedCount = async (): Promise<number> => {
    const response = await getRequest<{ count: number }>("/declarations/returns/unprocessed-count");
    return response.count;
  };

  /** Fetches the paginated overview of Vecozo credit requests, both treatment-wide and per declaration. */
  const fetchCreditRequests = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) =>
    getPaginationRequest<CreditRequestOverviewItem>(
      "/declarations/credit-requests",
      pagination,
      sortBy,
      includes,
      filterBy,
    );

  /** Fetches the count of credit requests that still wait for a Vecozo answer. */
  const fetchPendingCreditRequestsCount = async (): Promise<number> => {
    const response = await getRequest<{ data: { count: number } }>("/declarations/credit-requests/pending-count");
    return response.data.count;
  };

  /** Fetches a single invoice return by ID. */
  const fetchInvoiceReturn = async (returnId: string): Promise<InvoiceReturn> => {
    const response = await getRequest<Response<InvoiceReturn>>(`/declarations/returns/${returnId}`);
    return response.data;
  };

  /** Re-submits an invoice return with updated data. */
  const reSubmitInvoiceReturn = (
    returnId: number,
    data: {
      product_id: number;
      practitioner_id: string;
      practitioner_role_id: string;
      appointment_date: string;
      csi_code: string;
      authorization_number: string | null;
      update_treatment_authorization_number: boolean;
    },
  ) => postRequest<Response<InvoiceReturn>>(`/invoice/returns/${returnId}/re-submit`, data);

  const invoiceReturnProviderOptions = (returnId: number) =>
    getRequest<CollectionResponse<DeclarationProviderOption>>(`/invoice/returns/${returnId}/provider-options`);

  /** Converts an invoice return into a new invoice. */
  const convertInvoiceReturnToInvoice = (returnId: string) =>
    postRequest<Response<InvoiceReturn>>(`/invoice/returns/${returnId}/convert-to-invoice`, {});

  /** Marks an invoice return as processed. */
  const markInvoiceReturnAsProcessed = (returnId: string) =>
    postRequest<Response<InvoiceReturn>>(`/invoice/returns/${returnId}/mark-as-processed`, {});

  /** Updates a declaration's details. */
  const updateDeclaration = (declaration: DeclarationIdentifier, data: UpdateDeclarationPayload) =>
    putRequest<Response<Declaration>>(`/declarations/${declaration}`, data);

  const declarationProviderOptions = (declaration: DeclarationIdentifier) =>
    getRequest<CollectionResponse<DeclarationProviderOption>>(`/declarations/${declaration}/provider-options`);

  const declarationCorrectionProviderOptions = (declaration: DeclarationIdentifier) =>
    getRequest<CollectionResponse<DeclarationProviderOption>>(
      `/declarations/${declaration}/correction-provider-options`,
    );

  /** Retries a failed declaration submission. */
  const retryDeclaration = (declaration: DeclarationIdentifier) =>
    postRequest<Response<Declaration>>(`/declarations/${declaration}/retry`);

  /**
   * Corrects an approved declaration by submitting a single-session Vecozo credit request.
   * The corrected declaration is only created once Vecozo approves the credit.
   */
  const correctApprovedDeclaration = (
    declaration: DeclarationIdentifier,
    data: {
      product_id: number;
      practitioner_id: string;
      practitioner_role_id: string;
      appointment_date: string;
      csi_code: string;
    },
  ) => postRequest<Response<DeclarationCorrection>>(`/declarations/${declaration}/correct`, data);

  /** Fetches the latest correction (Vecozo credit request) status for a declaration. */
  const fetchDeclarationCorrectionStatus = (declaration: DeclarationIdentifier) =>
    getRequest<Response<DeclarationCorrection | null>>(`/declarations/${declaration}/correction`);

  /** Creates a to-do item for a declaration. */
  const createDeclarationTodo = (declaration: DeclarationIdentifier, data: CreateDeclarationTodoPayload) => {
    return postRequest<Response<Declaration>>(`/declarations/${declaration}/todo`, data);
  };

  /** Fetches a paginated list of declaration to-do items. */
  const fetchDeclarationTodos = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<DeclarationTodos>("/declarations/todos", pagination, sortBy, includes, filterBy);

  /** Fetches a paginated list of declaration rates, optionally filtered by year and type. */
  const fetchRates = (
    pagination: PaginationQuery,
    year?: number,
    sortBy?: SortByQuery,
    filterBy?: FilterByQuery,
    type?: string,
  ): Promise<PaginationResponse<Rate>> => {
    const params = {
      ...pagination,
      ...(sortBy && Array.isArray(sortBy) && sortBy.length > 0 && sortBy[0]
        ? { sort: sortBy[0].column, order: sortBy[0].direction }
        : !Array.isArray(sortBy) && sortBy
          ? { sort: sortBy.column, order: sortBy.direction }
          : {}),
      ...(filterBy && { filterBy }),
      ...(year && { year }),
      ...(type && { type }),
    };

    return getRequest<PaginationResponse<Rate>>(`/declarations/rates`, { params });
  };

  /** Creates a new declaration rate. */
  const storeRate = (data: {
    product_id: number;
    total_amount_in_cents: number;
    start_date: string;
    end_date: string;
  }) => postRequest<EmptyResponse>("/declarations/rates", data);

  /** Updates an existing declaration rate. */
  const updateRate = (data: {
    product_id: number;
    total_amount_in_cents: number;
    start_date: string;
    end_date: string;
  }) => patchRequest<EmptyResponse>(`/declarations/rates`, data);

  /** Fetches the active CSI codes, or also the expired ones with `includeExpired`. */
  const getCsiCodes = ({ includeExpired = false }: { includeExpired?: boolean } = {}) =>
    getRequest<CsiCode[]>("/declarations/csi-codes", includeExpired ? { params: { include_expired: 1 } } : undefined);

  /** Downloads a declaration invoice as a PDF file. */
  const downloadPdfInvoice = async (declaration: Declaration) => {
    try {
      const response = await getRequest<BlobPart>(`/invoice/pdf/${declaration.invoice_number}`, {
        responseType: "blob",
      });

      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${declaration.invoice_number}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error during downloading PDF:", error);
    }
  };

  /** Sends an invoice to the patient via email. */
  const sendInvoice = async (declaration: Declaration, options?: { markAsSubmittedWithoutEmail?: boolean }) => {
    try {
      return await postRequest<Response<{ email_sent: boolean }>>(`/invoice/${declaration.invoice_number}/send`, {
        mark_as_submitted_without_email: options?.markAsSubmittedWithoutEmail ?? false,
      });
    } catch (error) {
      console.error("Error during sending invoice:", error);
    }
  };

  const resendInvoice = (declaration: Declaration) =>
    postRequest<Response<{ email_sent: boolean; recipient: string | null }>>(
      `/invoice/${declaration.invoice_number}/resend`,
    );

  /** Submits an invoice for declaration processing. */
  const submitInvoice = async (declaration: Declaration) => {
    try {
      await postRequest<void>(`/invoice/${declaration.invoice_number}/submit`, {});
    } catch (error) {
      console.error("Error during sending invoice:", error);
    }
  };

  /** Credits a returned claim by issuing a credit invoice. */
  const creditReturnedClaim = async (declaration: Declaration) => {
    try {
      await postRequest<void>(`/invoice/${declaration.invoice_number}/credit`, {});
    } catch (error) {
      console.error("Error during crediting returned claim:", error);

      nextTick(() => {
        try {
          const nuxtApp = useNuxtApp();
          if (!nuxtApp.$pinia) return;
          const toast = useToast();
          const i18n = nuxtApp.$i18n as { t: (key: string) => string };
          toast.add({
            title: i18n.t("errors.errorTitle"),
            description: "Something went wrong",
            color: "error",
            icon: "i-heroicons-exclamation-triangle",
            duration: 5000,
          });
        } catch {
          // Pinia not ready
        }
      });
    }
  };

  /** Fetches the payment status for an invoice. */
  const paymentStatus = (invoiceId: string) =>
    getRequest<{
      invoice_id: string;
      invoice_number?: string;
      status: PaymentStatus;
      amount: number;
      payment_link?: string;
    }>(`/payment/${invoiceId}/status`);

  /** Creates a new invoice from a journal entry. */
  const createInvoice = (data: { journal_type: "treatment" | "intake"; journal_id: number }) =>
    postRequest<Response<{ id: string; invoice_number: string; total_amount: number }>>("/invoice", data);

  /** Previews invoice/declaration details for a journal before it is submitted. */
  const previewJournalInvoice = (data: {
    journal_type: "treatment" | "intake";
    appointment_id: number;
    treatment_id?: number;
    intake_id?: number;
    no_show?: boolean;
  }) => postRequest<Response<JournalInvoicePreview>>("/invoice/preview", data);

  /** Fetches invoice details by invoice number. */
  const getInvoiceDetails = (invoiceNumber: string) =>
    getRequest<Response<InvoiceDetails>>(`/invoice/${invoiceNumber}`);

  /** Fetches platform sync entries for a single invoice. */
  const getInvoicePlatformSyncsForInvoice = (invoiceNumber: string) =>
    getRequest<Response<InvoicePlatformSyncListItem[]>>(`/invoice/${invoiceNumber}/platform-syncs`).then(
      (response) => response.data,
    );

  /** Fetches a paginated list of all invoice platform syncs. */
  const fetchInvoicePlatformSyncs = (pagination: PaginationQuery, filterBy?: FilterByQuery) =>
    getPaginationRequest<InvoicePlatformSyncListItem>(
      "/invoice/platform-syncs",
      pagination,
      undefined,
      undefined,
      filterBy,
    );

  /** Fetches a single invoice platform sync by ID. */
  const getInvoicePlatformSync = (syncId: string) =>
    getRequest<Response<InvoicePlatformSyncListItem>>(`/invoice/platform-syncs/${syncId}`);

  return {
    validateDcsphCode,
    lookupDcsphCode,
    fetchDcsphCodes,
    fetchAllDcsphCodes,
    fetchDeclarationDashboardStats,
    notifyEmployeeMissingJournals,
    fetchMissingJournalAppointments,
    fetchToBeReinvoicedInvoices,
    reinvoiceCreditedInvoice,
    fetchDeclarations,
    fetchDeclarationTreatmentPractitioners,
    fetchDeclarationBatches,
    fetchDeclarationBatch,
    fetchDeclarationBatchRejectionXml,
    fetchDeclarationBatchRejectionTreatment,
    retryFailedDeclarationBatch,
    reprocessDeclarationBatch,
    fetchInvoiceReturns,
    fetchInvoiceReturnsUnprocessedCount,
    fetchCreditRequests,
    fetchPendingCreditRequestsCount,
    fetchInvoiceReturn,
    reSubmitInvoiceReturn,
    invoiceReturnProviderOptions,
    convertInvoiceReturnToInvoice,
    markInvoiceReturnAsProcessed,
    updateDeclaration,
    declarationProviderOptions,
    declarationCorrectionProviderOptions,
    retryDeclaration,
    correctApprovedDeclaration,
    fetchDeclarationCorrectionStatus,
    createDeclarationTodo,
    fetchDeclarationTodos,
    fetchRates,
    storeRate,
    updateRate,
    getCsiCodes,
    downloadPdfInvoice,
    sendInvoice,
    resendInvoice,
    submitInvoice,
    creditReturnedClaim,
    paymentStatus,
    createInvoice,
    previewJournalInvoice,
    getInvoiceDetails,
    getInvoicePlatformSyncsForInvoice,
    fetchInvoicePlatformSyncs,
    getInvoicePlatformSync,
  };
}

export type DeclarationApi = ReturnType<typeof createDeclarationApi>;
